import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useAppDispatch } from "../../../../Redux/hooks";
import { fetchMyOrders } from "../../../../Redux/custmoer_orders/slice";
import TrackingDialog from "../trackPackage/TrackingDialog";

const formatDate = (ts?: string) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount || 0);

const OrderItems = () => {
  const dispatch = useAppDispatch();
  const { orders, loading } = useSelector(
    (state: RootState) => state.customerOrders,
  );

  // AWB of the order whose tracking dialog is open (null = closed).
  const [trackAwb, setTrackAwb] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchMyOrders({ page: 1, limit: 10 }));
  }, [dispatch]);

  if (loading && orders.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!loading && orders.length === 0) {
    return (
      <div className="text-center py-5">
        <h5 className="text-muted">You have no orders yet.</h5>
      </div>
    );
  }

  return (
    <>
      <TrackingDialog
        open={!!trackAwb}
        awb={trackAwb ?? undefined}
        trackingId={trackAwb ?? undefined}
        onClose={() => setTrackAwb(null)}
      />

      {orders.map((order) => {
        const total = order.finalAmount?.finalPrice ?? order.payableAmount;
        return (
          <div className="mt-2 mb-2" key={order.orderId}>
            <div className=" card border-1">
              <div className=" card-header bg-gray-100">
                <div className=" d-flex justify-content-between align-items-center">
                  <div>
                    <div className=" d-flex justify-content-lg-start align-items-center gap-4">
                      <div>
                        <div>
                          <b className=" text-dark fw-bold text-sm text-uppercase">
                            Order Placed
                          </b>
                        </div>
                        <div>
                          <b className=" text-dark fw-normal text-sm text-uppercase">
                            {formatDate(order.orderDate)}
                          </b>
                        </div>
                      </div>
                      <span className=" text-gray opacity-2">|</span>
                      <div>
                        <div>
                          <b className=" text-dark fw-bold text-sm text-uppercase">
                            Total
                          </b>
                        </div>
                        <div>
                          <b className=" text-dark fw-normal text-sm text-uppercase">
                            {formatCurrency(total)}
                          </b>
                        </div>
                      </div>
                      <span className=" text-gray opacity-2">|</span>
                      <div>
                        <div>
                          <b className=" text-dark fw-bold text-sm text-uppercase">
                            Sold by
                          </b>
                        </div>
                        <div>
                          <b className=" fw-normal text-sm text-capitalize text-success">
                            {order.sellerDetails?.sellerName || "Salar Retail"}
                          </b>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className=" text-end">
                      <div>
                        <b className=" text-dark fw-bold text-sm text-uppercase">
                          Order # {order.orderNo}
                        </b>
                      </div>
                      <div className="s d-flex justify-content-center align-items-center gap-2">
                        <div>
                          <a
                            href={`/MaxOrderDetails?id=${order.orderId}`}
                            className=" fw-normal text-sm text-capitalize text-success"
                          >
                            View order details
                          </a>
                        </div>
                        {order.invoice && (
                          <>
                            |
                            <div>
                              <a
                                href={order.invoice}
                                target="_blank"
                                rel="noreferrer"
                                className=" fw-normal text-sm text-capitalize text-success"
                              >
                                Invoice
                              </a>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className=" card-body">
                <div className=" d-flex justify-content-between align-items-start">
                  <div className="w-70">
                    <div className="col-lg-12">
                      <div className=" lh-1 mb-3">
                        <b className=" text-dark fw-normal fs-4 text-capitalize">
                          {order.deliveryStatus
                            ? String(order.deliveryStatus).replace(/_/g, " ").toLowerCase()
                            : "Processing"}
                        </b>
                      </div>
                      {order.productDetails?.map((p, idx) => (
                        <div
                          className="d-flex justify-content-lg-start gap-0 align-items-center mb-2"
                          key={`${order.orderId}-${p.productId}-${idx}`}
                        >
                          <div className="w-20">
                            <img
                              src={p.productImage || "../../assets/img/placeholder.png"}
                              alt={p.productName || "product"}
                              className="cart-items-img"
                              loading="lazy"
                            />
                          </div>
                          <div className="w-80">
                            <div className="d-flex justify-content-between align-items-baseline">
                              <div>
                                <h6 className="text-md text-dark">
                                  {p.productName}
                                  {p.quantity ? `  ×${p.quantity}` : ""}
                                </h6>
                                <div className="mt-2">
                                  <div className=" d-flex justify-content-start align-items-center gap-2">
                                    <div>
                                      <a href={`/DetailPage?id=${p.productId}`}>
                                        <button className=" btn btn-outline-dark btn-sm border-radius-2xl">
                                          View your item{" "}
                                          <i className="bi bi-eye-fill ms-2 fs-6"></i>
                                        </button>
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1">
                      <button
                        className=" btn-outline-dark btn btn-sm border-radius-2xl w-100"
                        onClick={() =>
                          order.trackingNumber && setTrackAwb(order.trackingNumber)
                        }
                        disabled={!order.trackingNumber}
                        title={
                          order.trackingNumber
                            ? "Track this package"
                            : "Tracking not available yet"
                        }
                      >
                        Track Package
                      </button>
                    </div>
                    <div className="mb-1">
                      <a href="/SellerFeedback">
                        <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                          Seller Feedback
                        </button>
                      </a>
                    </div>
                    <div className="mb-1">
                      <a href="/DeliveryFeedback">
                        <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                          Delivery Feedback
                        </button>
                      </a>
                    </div>
                    <div className="mb-1">
                      <a href="/MaxProductReview">
                        <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                          Write a product review
                        </button>
                      </a>
                    </div>
                    <div className="mb-1">
                      <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                        Return or replace items
                      </button>
                    </div>
                    <div className="mb-1">
                      <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                        Cancel Order
                      </button>
                    </div>
                    <div className="mb-1">
                      <button className=" btn-outline-dark btn btn-sm border-radius-2xl w-100">
                        View Return/Refund Status
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card-footer border-top p-2">
                <a href="." className=" text-success fs-6">
                  Archive order
                </a>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default OrderItems;
